import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI, filenameFromDisposition, errorDetailFromBlob } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { DocumentChartBarIcon, FunnelIcon, ArrowDownOnSquareIcon,
         ExclamationCircleIcon, PhoneIcon } from '@heroicons/react/24/outline';

// Recorded call history. The operational Follow-ups queue remains in CV Screening.
const ACTIVITY_OUTCOMES = [
  { value: 'all',                 label: 'All outcomes' },
  { value: 'called_will_respond', label: 'Said they will respond' },
  { value: 'no_answer',           label: 'No answer' },
  { value: 'closed',              label: 'Closed — no response' },
];

const ACTIVITY_OUTCOME_STYLE = {
  called_will_respond: 'bg-green-100 text-green-700',
  no_answer:           'bg-amber-100 text-amber-800',
  closed:              'bg-red-100 text-red-700',
};

function fmtCalledAt(iso) {
  if (!iso) return { date: '—', time: '' };
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
  };
}

export default function FollowupActivityTab({ deptId, positions = [] }) {
  const { canModule } = useAuth();
  // Report access is independent of operational CV Screening permissions.
  const canRead = canModule('hr_reports', 'view');

  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();

  // Same filter set and the same order as HR Analytics Reports, with outcome
  // where that screen has status: From / To / position / outcome.
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo]     = useState(today);
  const [posFilter, setPosFilter]         = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      const res = await reportsAPI.followupActivity(deptId, {
        dateFrom,
        dateTo,
        // '' means "all positions"; the client drops empty values so the
        // parameter is absent rather than sent as a blank the server would
        // have to interpret.
        positionId: posFilter || undefined,
        outcome: outcomeFilter,
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load follow-up activity');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [canRead, deptId, dateFrom, dateTo, posFilter, outcomeFilter]);

  // Filters apply on change rather than behind an Apply button: this is a read,
  // it is one indexed query, and the HR Reports form only needs a button
  // because its submit GENERATES a PDF.
  useEffect(() => { load(); }, [load]);

  // EXPORT USES THE SAME FILTER STATE THE TABLE IS SHOWING — the same four
  // values passed to the same parameters, so the download cannot cover a
  // different period from the rows on screen. The server shares one fetch
  // between the JSON and PDF endpoints, so this is belt and braces, but the
  // belt is the part a reader of this file can see.
  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await reportsAPI.followupActivityPdf(deptId, {
        dateFrom,
        dateTo,
        positionId: posFilter || undefined,
        outcome: outcomeFilter,
      });
      // The SERVER's filename, read off the header. Null when the header did
      // not arrive (see filenameFromDisposition) — in which case a plain
      // generic name, never a reconstructed one that would look authoritative
      // while being a guess.
      const name = filenameFromDisposition(res.headers?.['content-disposition'])
        || 'followup-activity.pdf';
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      // A 403 (department scope) or 400 (bad parameter) arrives as a BLOB
      // because responseType is 'blob', so the detail has to be read out of it.
      // Shown inline in the same place a load error is shown — a failed export
      // that says nothing is indistinguishable from a click that did not register.
      setError(await errorDetailFromBlob(e, 'Could not export the PDF.'));
    } finally {
      setExporting(false);
    }
  };

  if (!canRead) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
        <DocumentChartBarIcon className="w-10 h-10 mx-auto text-gray-300" />
        <p className="mt-3 text-sm text-gray-600">
          This report needs the <span className="font-medium">HR — Reports</span> permission.
        </p>
        <p className="mt-1 text-xs text-gray-400">
          An admin can grant HR Reports access.
        </p>
      </div>
    );
  }

  const rows = data?.rows || [];

  return (
    <div className="space-y-4">
      {/* Filters — mirroring the HR Analytics Reports layout */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-green-600" />
              Follow-up Activity
            </h2>
            <p className="text-xs text-gray-500">
              Every follow-up call recorded for this department. Dates filter on when the
              call was made, not on when the candidate applied.
            </p>
          </div>
          {/* Disabled while EITHER request is in flight: exporting a range the
              table has not finished loading would hand the reader a document
              they cannot yet check against the screen. */}
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            className="shrink-0 flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownOnSquareIcon className="h-4 w-4 text-green-600" />
            {exporting ? 'Preparing PDF…' : 'Export PDF'}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || today}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              max={today}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Filter by position <span className="text-gray-400">(optional)</span>
            </label>
            {/* By id, not by title. A candidate can carry a position TITLE with
                no id — a failed match that renders like a successful one — and
                filtering on the string would quietly gather those in. */}
            <select
              value={posFilter}
              onChange={(e) => setPosFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200"
            >
              <option value="">All positions</option>
              {positions.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Filter by outcome <span className="text-gray-400">(optional)</span>
            </label>
            <select
              value={outcomeFilter}
              onChange={(e) => setOutcomeFilter(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-200"
            >
              {ACTIVITY_OUTCOMES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* The caveat that goes ALONGSIDE data, when the range reaches back past
          the day this table started recording. A partial period can hold calls
          and still be incomplete, and a reader must not take the rows for a
          total. The text comes from the server so this screen and the PDF
          cannot word it differently. */}
      {data?.coverage_note && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <ExclamationCircleIcon className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">{data.coverage_note}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      ) : rows.length === 0 ? (
        /* NEVER "0 calls". candidate_followups began on 19 Aug 2026 and will
           never backfill, so for an earlier range an empty table means NOT
           RECORDED — HR were phoning people for months before it existed — and
           for a later one it means nobody called. Those are different claims
           and the server decides which is true of the requested period. */
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <PhoneIcon className="w-10 h-10 mx-auto text-gray-300" />
          <p className="mt-3 text-sm text-gray-600 max-w-xl mx-auto">
            {data?.empty_text || 'No calls recorded in this period.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-xs text-gray-600">
            {data.total} {data.total === 1 ? 'call' : 'calls'} recorded
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Candidate</th>
                  <th className="px-4 py-2 text-left font-medium">Position</th>
                  <th className="px-4 py-2 text-left font-medium">Called</th>
                  <th className="px-4 py-2 text-left font-medium">By</th>
                  <th className="px-4 py-2 text-left font-medium">Outcome</th>
                  <th className="px-4 py-2 text-left font-medium">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((r) => {
                  const when = fmtCalledAt(r.contacted_at);
                  return (
                    <tr key={r.id}>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {r.candidate_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="block truncate max-w-[16rem]"
                              title={r.position_title || 'No position'}>
                          {r.position_title || 'No position'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-gray-700">{when.date}</div>
                        <div className="text-xs text-gray-400">{when.time}</div>
                      </td>
                      <td className="px-4 py-3">
                        {/* contacted_by is ON DELETE SET NULL, so a call can
                            outlive the person who made it. That is a missing
                            NAME, not a call that nobody made, and it says so
                            rather than rendering an empty cell. */}
                        {r.contacted_by_name ? (
                          <>
                            <div className="text-gray-700">{r.contacted_by_name}</div>
                            <div className="text-xs text-gray-400 truncate max-w-[14rem]"
                                 title={r.contacted_by_email || ''}>
                              {r.contacted_by_email}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic">User removed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          ACTIVITY_OUTCOME_STYLE[r.outcome] || 'bg-gray-100 text-gray-700'
                        }`}>
                          {/* Rendered server-side, so an outcome added later
                              reads as itself instead of vanishing. */}
                          {r.outcome_label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {r.note
                          ? <span className="block max-w-[22rem]">{r.note}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
